import { paginated_factory_v2, backend_api_v2 } from '/src/utils/apiv2'
import base from "./base"
import { defineStore } from 'pinia'
import bus from '/src/utils/event_bus'
import { drop_cloud_file } from '/src/utils/cloudfiles'

let LASTS = {}

const PACKAGE_KEYS = [
    "company_packages",
    "project_packages",
    "module_packages",
    "assembly_packages",
    "module_design_packages",
    "assembly_design_packages",
    "parent_assembly_design_packages",
]

// Docs packages asked for by rows that mount in the same tick, flushed as one
// `?uuid__in=` request. Same shape as the list-hint queue in `constructibles`.
let _docs_queue = []
let _docs_timeout = null
const DOCS_FLUSH_DELAY_MS = 50

const LOAD_RETRIES = 2      // extra attempts after a failed load
const RETRY_DELAY_MS = 2000

function loadPackagesFactory(keyword, api_query_level, slim = true, error_message = null) {
    if (!api_query_level) {
        api_query_level = keyword;
    }
    const slimParam = slim ? "&no_presign=true" : "";

    const loadPackages = function (uuid, attempt = 0) {

        // Register last call (ensure last called fills)
        LASTS[keyword] = uuid
        this.packages_loading[keyword] = true
        this.packages_error[keyword] = false

        // Fill it
        return paginated_factory_v2(`/cloudstorage/packages/?${api_query_level}=${uuid}${slimParam}`)
            .then(({ data }) => {
                // Accept last
                if (LASTS[keyword] == uuid)
                    this[`set_${keyword}_packages`](data)
                // Reject prebounce
                else console.log("[i] Rejecting loadPackages commit. Not last call.")
                return data
            })
            .catch(e => {
                console.log(`Cant load packages: ${e}`)
                // Superseded by a newer call: that call owns status/notifications.
                if (LASTS[keyword] != uuid) return
                if (attempt < LOAD_RETRIES) {
                    console.log(`[i] Retrying loadPackages (${attempt + 1}/${LOAD_RETRIES})`)
                    return new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
                        .then(() => {
                            if (LASTS[keyword] == uuid)
                                return loadPackages.call(this, uuid, attempt + 1)
                        })
                }
                this.packages_error[keyword] = true
                // Canceled requests (logout, auth not ready yet) are not
                // backend failures — don't notify the user about those.
                if (error_message && e.code != "ERR_CANCELED")
                    bus.emit("notification", { text: error_message, color: "error", timeout: 5000 })
            })
            .finally(() => {
                // Runs after the whole retry chain settles (the catch above
                // returns the retry promise), so this never hides an
                // in-flight retry.
                if (LASTS[keyword] == uuid)
                    this.packages_loading[keyword] = false
            })
    }
    return loadPackages
}


// True when `file` carries a non-empty presigned URL that hasn't expired yet.
// `presigned_url_expires_at` may be missing in older payloads — assume fresh
// in that case (graceful fallback).
export function isUrlFresh(file) {
    if (!file?.presigned_url) return false
    if (!file.presigned_url_expires_at) return true
    return new Date(file.presigned_url_expires_at) > new Date()
}


// An aborted request is not a backend failure: the request interceptor blocks
// and aborts every call the moment the session is gone (logout, expired token
// on a tablet waking up), so whatever was refreshing in the background at that
// moment settles as canceled. Same reading as loadPackagesFactory above.
function isCanceled(e) {
    return e?.code === "ERR_CANCELED" || e?.name === "CanceledError"
}


function cleanDuplicatesInPackage(uuid) {
    // Warning, this doesn't update store. Needs proper update.
    return backend_api_v2.post(`/cloudstorage/packages/${uuid}/clean_duplicates/`)
        .then(({ data }) => {
            return { data }
        })
        .catch(e => console.log(`Cant clean package: ${e}`))
}


function deleteCloudFileByPckAndName(payload) {
    // Warning, this doesn't update store. Needs proper update.
    return backend_api_v2.post(`/cloudstorage/packages/${payload.pck}/clean_files_by_name/`, { name: payload.name })
        .then(({ data }) => {
            return { data }
        })
        .catch(e => console.log(`Cleant package: ${e}`))
}



export const useCloudstorageStore = defineStore('cloudstorage', {
    state: () => ({
        // The files ticked in the project's Files table. Update designs, the
        // page's one button, works on them when there are any.
        file_selection: [],

        // Signed thumbnail URLs by CloudFile uuid, for this session only.
        // They last 10 hours server-side, which outlives any shift on one
        // panel, and asking again is one request rather than one per tile.
        thumb_urls: {},

        // Checklist docs packages by uuid. They are station configuration, the
        // same on every panel of the station, so they are fetched once per
        // session rather than once per task per panel opened. `null` means the
        // package was asked for and does not exist (or is not ours).
        docs_packages: {},

        company_packages: [],
        project_packages: [],
        module_packages: [],
        assembly_packages: [],
        module_design_packages: [],
        assembly_design_packages: [],
        parent_assembly_design_packages: [],
        // Per-keyword fetch status ("project", "company", ...) so views can
        // show a loading state and recover from failed loads.
        packages_loading: {},
        packages_error: {},
    }),
    getters: {
        packagesByNameAndLevel: state => ({ name, level }) => {
            const list = state[`${level}_packages`] || []
            if (name == null) return list
            else if (Array.isArray(name))
                return list.filter(pckg => name.includes(pckg.name))
            else
                return list.filter(pckg => pckg.name == name)
        },
        cloudFileUuidByUrl: (state) => (url) => {
            for (const key of PACKAGE_KEYS) {
                for (const pkg of state[key]) {
                    const cf = pkg.files?.find(f => f.presigned_url === url)
                    if (cf) return cf.uuid
                }
            }
            return null
        },
        // The CloudFile itself, so a consumer holding only a uuid can check
        // how fresh its URL is before using it. Null when the file was not
        // loaded through the package lists (a direct package fetch, say).
        cloudFileByUuid: (state) => (uuid) => {
            for (const key of PACKAGE_KEYS) {
                for (const pkg of state[key]) {
                    const cf = pkg.files?.find(f => f.uuid === uuid)
                    if (cf) return cf
                }
            }
            return null
        },
        resourcesPackage: state => state.project_packages.find((pck) => pck.name == "resources"),


        // Full project last revisions. Same sections the General Arrangement
        // page (ProjectViewer.vue) draws, so its entry only shows when the
        // page has something to show.
        hasFullProjectResources: (state) => {
            let files = state.resourcesPackage?.files || []
            let pr_files = files
                .filter(cf => ["project_ifc", "project_drawing"]
                    .includes(cf.meta?.section))
            return pr_files.length != 0
        },
    },
    actions: {
        set_company_packages(data) { this.company_packages = data },
        set_project_packages(data) { this.project_packages = data },
        set_module_packages(data) { this.module_packages = data },
        set_assembly_packages(data) { this.assembly_packages = data },
        set_module_design_packages(data) { this.module_design_packages = data },
        set_assembly_design_packages(data) { this.assembly_design_packages = data },
        set_parent_assembly_design_packages(data) { this.parent_assembly_design_packages = data },
        // No panel to show: empty the list and drop any load still on its way,
        // which would otherwise commit an earlier panel's packages.
        clearParentAssemblyDesignPackages() {
            LASTS["parent_assembly_design"] = null
            this.parent_assembly_design_packages = []
        },

        // Called by name from `base.actions.deleter` ('remove' + 'CloudFile')
        // and by `deleteCloudFiles`. A file can sit in several packages, so it
        // leaves all of them. It used to test `"files" in` each list, which is
        // an array, so it never removed anything.
        removeCloudFile(uuid) {
            drop_cloud_file(PACKAGE_KEYS.map(k => this[k]), uuid)
        },

        loadCompanyPackages: loadPackagesFactory("company"),
        loadProjectPackages: loadPackagesFactory(
            "project", null, true,
            "Failed fetching resources, reload this project."),
        loadModulePackages: loadPackagesFactory("module"),
        loadAssemblyPackages: loadPackagesFactory("assembly"),
        // Design packages stay un-slim — presigned URLs are pre-baked into
        // the response so design viewers/lists can render without any
        // on-the-fly refresh roundtrip.
        loadModuleDesignPackages: loadPackagesFactory("module_design", null, false),
        loadAssemblyDesignPackages: loadPackagesFactory("assembly_design", null, false),
        loadParentAssemblyDesignPackages: loadPackagesFactory("parent_assembly_design", "assembly_design", false),

        deleteCloudFile: base.actions.deleter("cloudstorage/files/", "CloudFile"),
        deleteCloudFiles(uuids) {
            return backend_api_v2.post('cloudstorage/bulk_delete_files/', uuids)
                .then(() => {
                    uuids.forEach(uuid => this.removeCloudFile(uuid))
                })
                .catch(e => console.log(`Cant bulk delete CloudFiles: ${e}`))
        },

        async refreshCloudFile(uuid) {
            const { data } = await backend_api_v2.get(`cloudstorage/files/${uuid}/`)
            for (const key of PACKAGE_KEYS) {
                for (const pkg of this[key]) {
                    const cf = pkg.files?.find(f => f.uuid === uuid)
                    if (cf) {
                        cf.presigned_url = data.presigned_url
                        // Must update expiry too — isUrlFresh checks both, so
                        // leaving the old past expiry in place would make the
                        // file look stale immediately after refresh and put
                        // ensurePresignedUrls into an infinite refresh loop.
                        cf.presigned_url_expires_at = data.presigned_url_expires_at
                        return data.presigned_url
                    }
                }
            }
            return data.presigned_url
        },

        // Resolve a presigned URL for `file`. Returns the cached URL if it's
        // still valid; otherwise fetches a fresh one via /cloudstorage/files/
        // (one IAM call, on user demand).
        async getPresignedUrl(file) {
            if (isUrlFresh(file)) return file.presigned_url
            if (!file?.uuid) return ""
            try {
                return await this.refreshCloudFile(file.uuid)
            } catch (e) {
                // Answer the same empty URL every caller already guards for.
                // Several of them are fire-and-forget (the BOM loader, an
                // @click handler), so rejecting here is an unhandled rejection
                // and nothing else — and there is nothing to report anyway,
                // the app is already on its way to the login page. A real
                // failure still throws.
                if (isCanceled(e)) return ""
                throw e
            }
        },

        // Fire-and-forget background refresh of any file in `files` whose
        // cached URL is missing or expired. The store mutation in
        // refreshCloudFile populates file.presigned_url reactively so
        // consumers can read it directly. Accepts a flat list of CloudFile
        // objects — callers extract the files they care about from packages.
        ensurePresignedUrls(files) {
            if (!files) return
            for (const f of files) {
                if (f && f.uuid && !isUrlFresh(f))
                    this.refreshCloudFile(f.uuid).catch(e => {
                        // Nothing awaits these, so an error that escapes is an
                        // unhandled rejection. The file keeps its stale URL and
                        // the next getPresignedUrl asks for it again.
                        if (!isCanceled(e))
                            console.warn(`Cant refresh CloudFile: ${e}`)
                    })
            }
        },
        /** Make sure the docs package `uuid` is in `docs_packages`.
         *
         * Rows that mount together are batched into one request. Fetched
         * without presigning: the URL is only signed when someone opens the
         * document (`docsFileUrl`).
         */
        fetchDocsPackage(uuid) {
            if (!uuid || uuid in this.docs_packages || _docs_queue.includes(uuid)) return
            _docs_queue.push(uuid)
            if (_docs_timeout) clearTimeout(_docs_timeout)
            _docs_timeout = setTimeout(() => this._flushDocsPackages(), DOCS_FLUSH_DELAY_MS)
        },
        async _flushDocsPackages() {
            const uuids = [..._docs_queue]
            _docs_queue = []
            _docs_timeout = null
            if (!uuids.length) return
            try {
                const { data } = await paginated_factory_v2(
                    `/cloudstorage/packages/?uuid__in=${uuids.join(",")}&no_presign=true`)
                const found = Object.fromEntries(data.map(pck => [pck.uuid, pck]))
                const answered = Object.fromEntries(uuids.map(u => [u, found[u] || null]))
                this.docs_packages = { ...this.docs_packages, ...answered }
            } catch (e) {
                // Left out of the cache, so the next row that mounts asks again.
                if (!isCanceled(e)) console.warn(`Cant load docs packages: ${e}`)
            }
        },
        /** A signed URL for the first file of docs package `uuid`, signing it only when needed. */
        async docsFileUrl(uuid) {
            const cf = this.docs_packages[uuid]?.files?.[0]
            if (!cf) return ""
            if (isUrlFresh(cf)) return cf.presigned_url
            const { data } = await backend_api_v2.get(`cloudstorage/files/${cf.uuid}/`)
            // Kept on the cached file, so the next panel of the station reuses it.
            cf.presigned_url = data.presigned_url
            cf.presigned_url_expires_at = data.presigned_url_expires_at
            return data.presigned_url
        },

        /** Fetch the cached docs packages again. Called by *Resync*.
         *
         * Replaced when the answer arrives rather than emptied first: the rows
         * asked once on mount and would not ask again. One request, since the
         * cache only ever holds the docs of the stations visited.
         */
        async clearCaches() {
            const docs = Object.keys(this.docs_packages)
            if (!docs.length) return
            try {
                const { data } = await paginated_factory_v2(
                    `/cloudstorage/packages/?uuid__in=${docs.join(",")}&no_presign=true`)
                const found = Object.fromEntries(data.map(pck => [pck.uuid, pck]))
                this.docs_packages = Object.fromEntries(docs.map(u => [u, found[u] || null]))
            } catch (e) {
                if (!isCanceled(e)) console.warn(`Cant refresh docs packages: ${e}`)
            }
        },

        /** Fill `thumb_urls` for `files` that have no URL yet.
         *
         * One request for a whole card, not one per tile. The server builds
         * what is missing, up to its own budget, so a panel full of old
         * pictures fills in over a couple of visits instead of holding a
         * worker while it shrinks all of them at once.
         *
         * Files it answers nothing for keep no entry, and the row draws a
         * plain tile: a picture in a format we cannot open never gets one.
         */
        async loadThumbUrls(files) {
            const uuids = (files || [])
                .map(f => f?.uuid)
                .filter(uuid => uuid && !this.thumb_urls[uuid])
            if (!uuids.length) return
            try {
                const { data } = await backend_api_v2.post(
                    'cloudstorage/files/thumbs/', { uuids })
                Object.assign(this.thumb_urls, data || {})
            } catch (e) {
                // A tile without a thumbnail is a tile, not an error.
                console.warn('Could not load thumbnails', e)
            }
        },

        deleteCloudFileByPckAndName,
        cleanDuplicatesInPackage,
    },
})
